import { useEffect } from "react";
import { toEpochSeconds } from "../domain/time";
import { countOpenDaysToCorrect } from "../offline/localTimeline";
import { useClockSnapshot, useOffline } from "../offline/OfflineContext";
import { useWorkTimeZone } from "../shared/session/useWorkTimeZone";
import { useNow } from "../shared/ui/useNow";
import { usePlatform } from "./PlatformContext";
import { useOwnerKey } from "../shared/session/tenantSelection";

/** How often the badge follows the change of the day and the server's thresholds (F-13). */
const badgeRefreshMs = 60_000;

/**
 * Keeps the icon badge of the app at the number of open working days to correct, the days the
 * clock screen lists (spec 12.2, T-082, F-14), from the instant the server reports them as
 * incomplete (F-13), in the time zone of the clock screen (F-18: the employment's, before `GET /me`
 * answers the one stored with the last profile). Removes the delivered reminders only once
 * the server reports no open working day: a day the badge does not count yet can still be open on
 * the server. Works offline on the stored server snapshot; renders nothing.
 *
 * @returns `null`.
 */
export function OpenDaysBadge(): null {
  const platform = usePlatform();
  const offline = useOffline();
  const owner = useOwnerKey();
  const snapshot = useClockSnapshot(owner);
  const nowMs = useNow(offline.nowMs, badgeRefreshMs);
  // While the profile loads and no zone is stored, the count waits instead of starting in the
  // device's time zone.
  const timeZone = useWorkTimeZone();
  const known = snapshot ?? null;
  const count =
    known === null || timeZone === null
      ? null
      : countOpenDaysToCorrect(known, toEpochSeconds(nowMs), timeZone);
  const serverDaysClosed = known === null ? null : known.clockState.openDays.length === 0;

  useEffect(() => {
    if (count !== null) {
      void platform.setBadge(count);
    }
  }, [platform, count]);

  useEffect(() => {
    if (serverDaysClosed === true) {
      void platform.clearDeliveredReminders();
    }
  }, [platform, serverDaysClosed]);

  return null;
}
